import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { DriveUpRow, ENTRANCE, IsoMarketScene, Shadow, entranceTimeline, marketPieces, type MarketPiece, TRUCKS, Tower, TruckBody, driveClass, iso, pts, row, tower, truckBox, type Building, type Facility, type MarketInLandscape } from "./IsoMarket";

// The brand book keeps a copy (app/src/studio/brand/IsoLandscape.tsx); keep the two in step.

// The hero's full-page landscape: the brand book's isometric market, set in open country. Fields,
// trees, water, farmhouses and a few more competitors spread out from the market to the edges of the
// page, fade into a warm sky under blue mountains, and unfold outward from our lot on load.
// Straight lines only, like the market itself.

type PlanePoint = [number, number];
type Rect = [number, number, number, number];

function mulberry32(seed: number) {
  let a = seed;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// The market occupies the plane from 0 to 20; its roads run on through the country.
const WORLD: Rect = [-62, -62, 64, 64];
const MARKET: Rect = [-1.2, -1.2, 21.2, 21.2];
const CENTRE: PlanePoint = [10, 10];
const ROAD_NS: [number, number] = [9, 10.4];
const ROAD_EW: [number, number] = [9, 10.4];
const LANE: Rect = [10.4, 34, 64, 35];

// The lake's bounds (for keeping fields and trees clear) and its shoreline: a loose oval.
const LAKE: Rect = [-15, 25, -4, 34];
const LAKE_CENTRE: PlanePoint = [-9.5, 29.5];
const LAKE_SHORE: PlanePoint[] = (() => {
  const rand = mulberry32(8);
  return Array.from({ length: 11 }, (_, i) => {
    const a = (i / 11) * Math.PI * 2;
    const r = 1 + (rand() - 0.5) * 0.24;
    return [LAKE_CENTRE[0] + Math.cos(a) * 5.5 * r, LAKE_CENTRE[1] + Math.sin(a) * 4.4 * r] as PlanePoint;
  });
})();
// The rivers' courses, drawn as smooth bends; RIVER below is their footprint.
const RIVER_COURSES: PlanePoint[][] = [
  [[-62, 38.1], [-28.9, 38.1], [-28.9, 29.5], [-13, 29.5]],
  [[-9.5, 32], [-9.5, 64]],
];
const RIVER_WIDTH = 1.8;
// How far a plane point is from the lake's centre, in shore radii (1 = on the shore, roughly).
const lakeReach = ([x, y]: PlanePoint) => Math.hypot((x - LAKE_CENTRE[0]) / 5.5, (y - LAKE_CENTRE[1]) / 4.4);
// Trees just back from the far shores (north and west, away from the river's mouth), so their
// reflections fall on the water.
const LAKESIDE: PlanePoint[] = [150, 162, 205, 222, 238, 252, 266, 280, 294, 308, 322].map((deg) => {
  const a = (deg * Math.PI) / 180;
  return [LAKE_CENTRE[0] + Math.cos(a) * 5.5 * 1.16, LAKE_CENTRE[1] + Math.sin(a) * 4.4 * 1.16];
});
// Rivers as runs of axis-aligned rectangles.
const RIVER: Rect[] = [
  [-62, 37.2, -28, 39],
  [-29.8, 28.6, -28, 39],
  [-29.8, 28.6, -13, 30.4],
  [-10.4, 32, -8.6, 64],
];

// The country's colours: tints of the brand blue, with wheat gold. Nothing in the landscape is drawn
// with a line: shapes are told apart by their lit and shaded faces alone. SOFT_EDGES also takes the
// edges and detail lines off the market's buildings.
interface Theme {
  sky: [string, string, string];
  ridge: [string, string];
  ground: string;
  crops: Record<"wheat" | "pasture" | "meadow" | "forest", { fill: string; line: string }>;
  tree: { light: string; shade: string; edge: string; trunk: string };
  water: { far: string; mid: string; near: string; bank: string; reflection: string };
  road: { fill: string; edge: string; dash: string };
  pad: string;
  ourPad: string;
  roof: { slope: string; edge: string; back: string };
}

const MIST: Theme = {
  sky: ["#ffffff", "#f6f9fa", "#e8f0f3"],
  ridge: ["#e3ecf0", "#d0dfe6"],
  ground: "#d8e4e9",
  crops: { wheat: { fill: "#e9c878", line: "#d8b259" }, pasture: { fill: "#d3e1e7", line: "#c3d5dd" }, meadow: { fill: "#e2ecf0", line: "" }, forest: { fill: "#c3d6de", line: "" } },
  tree: { light: "#6f9cb0", shade: "#507f96", edge: "#3f6d83", trunk: "#6b7f8a" },
  water: { far: "#c3dae4", mid: "#a8c8d6", near: "#8db3c6", bank: "#4f7f97", reflection: "#6f98ae" },
  road: { fill: "#ffffff", edge: "#d6e0e5", dash: "#cbd5e1" },
  pad: "#e9eff2",
  ourPad: "#d8e4ea",
  roof: { slope: "#6f8a99", edge: "#5a7482", back: "#b3c3cd" },
};

const SOFT_EDGES = `
  .ls-soft polygon { stroke: none !important; }
  .ls-soft line { display: none; }
`;

const ThemeContext = createContext<Theme>(MIST);

const CROPS = MIST.crops;
type Crop = keyof typeof CROPS;
const CROP_ODDS: [Crop, number][] = [["wheat", 0.26], ["pasture", 0.38], ["meadow", 0.22], ["forest", 0.14]];

const overlaps = ([a0, b0, a1, b1]: Rect, [c0, d0, c1, d1]: Rect) => a1 > c0 && a0 < c1 && b1 > d0 && b0 < d1;
const inside = ([x, y]: PlanePoint, [x0, y0, x1, y1]: Rect) => x > x0 && x < x1 && y > y0 && y < y1;
const grow = ([x0, y0, x1, y1]: Rect, by: number): Rect => [x0 - by, y0 - by, x1 + by, y1 + by];
const quad = ([x0, y0, x1, y1]: Rect, z = 0) => pts([iso(x0, y0, z), iso(x1, y0, z), iso(x1, y1, z), iso(x0, y1, z)]);

const ROADS: Rect[] = [
  [ROAD_NS[0], WORLD[1], ROAD_NS[1], WORLD[3]],
  [WORLD[0], ROAD_EW[0], WORLD[2], ROAD_EW[1]],
  LANE,
];
const WATER: Rect[] = [LAKE, ...RIVER];

// Rival storage sites, all from one template on a regular grid of blocks along the roads: a pad set
// back from the road, drive-up rows at an even pitch, and on some a climate-controlled building at
// the back.
const PAD_INSET = 0.8;
const ROW_DEPTH = 1.1;
const ROW_PITCH = 2.3;

function site(lot: Rect, along: "x" | "y", price: string, climate = false, ours = false, floors = 2): Facility {
  const [x0, y0, x1, y1] = lot;
  const buildings: Building[] = [];
  const m = PAD_INSET;
  if (along === "x") {
    let y = y0 + m;
    if (climate) {
      buildings.push(tower(x0 + m, y, x1 - x0 - 2 * m, 2.6, ours, floors, [0]));
      y += 2.6 + 1.3;
    }
    for (; y + ROW_DEPTH <= y1 - m + 0.01; y += ROW_PITCH) buildings.push(row(x0 + m, y, x1 - x0 - 2 * m, ROW_DEPTH, ours, "left", buildings.length === 1 ? [2] : undefined));
  } else {
    for (let x = x0 + m; x + ROW_DEPTH <= x1 - m + 0.01; x += ROW_PITCH) buildings.push(row(x, y0 + m, ROW_DEPTH, y1 - y0 - 2 * m, ours, "right", buildings.length === 1 ? [1] : undefined));
  }
  return { lot, ours, buildings, flagOn: ours ? 0 : Math.floor(buildings.length / 2), price };
}

// Ours: the same kind of site in navy, its climate-controlled building a floor taller, flying both
// of its rates. Its market reaches the three nearest rivals.
const OURS: Facility = { ...site([0.6, 0.6, 8.4, 8.4], "x", "", true, true, 3), price: ["$141", "$152"] };

// The three rivals around our lot, inside the market, flagged with their rates.
const RIVALS: Facility[] = [
  site([0.6, 11.2, 8.4, 19], "x", "$136"),
  site([11.2, 0.6, 19, 8.4], "x", "$158", true),
  site([11.2, 11.2, 19, 19], "y", "$162"),
];
// More of them further out, on the same grid.
const OUTER: Facility[] = [
  site([11.2, 22.8, 19, 30.6], "y", ""),
  site([22.8, 11.2, 30.6, 19], "x", "", true),
  site([11.2, -11.4, 19, -3.6], "y", ""),
];
const LOTS = OUTER.map((f) => ({ lot: f.lot }));

/** The hero's market entrance: when the last building lands, the flags start, and it has settled.
 *  The website times its scroll cue from it. */
export const LANDSCAPE_ENTRANCE = entranceTimeline([OURS, ...RIVALS]);
const MARKET_PIECES = marketPieces(OURS, RIVALS);

const HOUSES: PlanePoint[] = [[2, 24.4], [5.2, 25], [26, 5.2], [24.6, 20.6], [-4.4, 4.4], [14, 37], [22, 37.4], [-18, 11.6], [3, -6], [33, 23]];

interface Parcel {
  rect: Rect;
  crop: Crop;
  alongX: boolean;
}

interface Tree {
  at: PlanePoint;
  size: number;
}

function buildCountry() {
  const rand = mulberry32(20);
  const parcels: Parcel[] = [];
  const trees: Tree[] = [];
  const pick = () => {
    let r = rand();
    for (const [crop, odds] of CROP_ODDS) {
      r -= odds;
      if (r <= 0) return crop;
    }
    return "meadow" as Crop;
  };
  // Strips across the plane, each cut into fields of its own lengths.
  for (let x = WORLD[0]; x < WORLD[2]; ) {
    const width = 5 + rand() * 7;
    for (let y = WORLD[1]; y < WORLD[3]; ) {
      const length = 4 + rand() * 9;
      const rect: Rect = [x, y, Math.min(WORLD[2], x + width), Math.min(WORLD[3], y + length)];
      // A field cut by a road or the water keeps the part that's clear of it.
      const parts = splitClear(rect);
      for (const part of parts) {
        if (part[2] - part[0] < 1.2 || part[3] - part[1] < 1.2) continue;
        const crop = pick();
        parcels.push({ rect: part, crop, alongX: rand() < 0.5 });
        if (crop === "forest") {
          for (let tx = part[0] + 0.6; tx < part[2] - 0.3; tx += 1.15) {
            for (let ty = part[1] + 0.6; ty < part[3] - 0.3; ty += 1.15) {
              if (rand() < 0.82) trees.push({ at: [tx + (rand() - 0.5) * 0.5, ty + (rand() - 0.5) * 0.5], size: 0.8 + rand() * 0.45 });
            }
          }
        } else if (rand() < 0.35) {
          // A hedgerow of trees along one edge.
          const along = rand() < 0.5;
          const [x0, y0, x1, y1] = part;
          for (let t = 0.5; t < (along ? x1 - x0 : y1 - y0) - 0.3; t += 1.3 + rand()) {
            if (rand() < 0.7) trees.push({ at: along ? [x0 + t, y1 - 0.2] : [x1 - 0.2, y0 + t], size: 0.75 + rand() * 0.35 });
          }
        }
      }
      y += length;
    }
    x += width;
  }
  // Trees on the lake's far shores (north and west): their reflections fall on the water.
  for (const at of LAKESIDE) trees.push({ at, size: 0.8 + rand() * 0.35 });
  // Trees along the water.
  for (const [x0, y0, x1, y1] of WATER) {
    for (let t = 0; t < Math.max(x1 - x0, y1 - y0); t += 1.6 + rand() * 1.6) {
      if (rand() < 0.55) {
        const at: PlanePoint = x1 - x0 > y1 - y0 ? [x0 + t, y1 + 0.7] : [x1 + 0.7, y0 + t];
        trees.push({ at, size: 0.8 + rand() * 0.4 });
      }
    }
  }
  const clearOfBuildings = (t: Tree) =>
    !inside(t.at, grow(MARKET, 0.8)) &&
    !ROADS.some((r) => inside(t.at, grow(r, 0.6))) &&
    !RIVER.some((w) => inside(t.at, grow(w, 0.4))) &&
    lakeReach(t.at) > 1.1 &&
    !LOTS.some((l) => inside(t.at, grow(l.lot, 0.8))) &&
    !HOUSES.some(([hx, hy]) => inside(t.at, [hx - 1, hy - 1, hx + 2.6, hy + 2.2]));
  return { parcels, trees: trees.filter(clearOfBuildings) };
}

/** The parts of a field clear of the market, roads, water and lots (cut along the obstacle). */
function splitClear(rect: Rect): Rect[] {
  const obstacles = [grow(MARKET, 0.4), ...ROADS.map((r) => grow(r, 0.35)), ...WATER.map((w) => grow(w, 0.35)), ...LOTS.map((l) => grow(l.lot, 0.4))];
  let parts: Rect[] = [rect];
  for (const [ox0, oy0, ox1, oy1] of obstacles) {
    const next: Rect[] = [];
    for (const p of parts) {
      const [x0, y0, x1, y1] = p;
      if (!overlaps(p, [ox0, oy0, ox1, oy1])) {
        next.push(p);
        continue;
      }
      if (y0 < oy0) next.push([x0, y0, x1, oy0]);
      if (y1 > oy1) next.push([x0, oy1, x1, y1]);
      const my0 = Math.max(y0, oy0);
      const my1 = Math.min(y1, oy1);
      if (x0 < ox0) next.push([x0, my0, ox0, my1]);
      if (x1 > ox1) next.push([ox1, my0, x1, my1]);
    }
    parts = next;
  }
  return parts;
}

const COUNTRY = buildCountry();

// ---------------------------------------------------------------------------
// Pieces

function Field({ p }: { p: Parcel }) {
  const { fill } = useContext(ThemeContext).crops[p.crop];
  return (
    <polygon data-kind={`field ${p.crop}`} points={quad(p.rect)} fill={fill} />
  );
}

function octagon([x0, y0, x1, y1]: Rect, cut: number): PlanePoint[] {
  return [[x0 + cut, y0], [x1 - cut, y0], [x1, y0 + cut], [x1, y1 - cut], [x1 - cut, y1], [x0 + cut, y1], [x0, y1 - cut], [x0, y0 + cut]];
}


// Shadows fall to the right, like the buildings'. They're drawn together on the ground in one softly
// blurred layer (#ls-soft), so none has a hard edge.
const SHADOW = "rgba(20,45,62,0.14)";

function TreeShadow({ t }: { t: Tree }) {
  const [x, y] = t.at;
  const s = t.size;
  const r = 0.34 * s;
  return (
    <polygon points={pts([iso(x - r * 0.6, y - r), iso(x + 1.1 * s, y - r + 0.35 * s), iso(x + 1.1 * s, y + r + 0.35 * s), iso(x - r * 0.6, y + r)])} fill={SHADOW} />
  );
}

/** A tree: a slim four-sided spire, lit from the left like the buildings. */
function TreeShape({ t }: { t: Tree }) {
  const theme = useContext(ThemeContext).tree;
  const [x, y] = t.at;
  const s = t.size;
  const r = 0.34 * s;
  const apex = iso(x, y, 1.7 * s);
  return (
    <g data-kind="tree">
      <polygon points={pts([iso(x - r, y + r), iso(x + r, y + r), apex])} fill={theme.light} />
      <polygon points={pts([iso(x + r, y + r), iso(x + r, y - r), apex])} fill={theme.shade} />
    </g>
  );
}

const HOUSE = { w: 1.7, d: 1.2, h: 0.7 };

function HouseShadow({ at: [x, y] }: { at: PlanePoint }) {
  const { w, d } = HOUSE;
  return <polygon points={pts([iso(x + w, y), iso(x + w + 1.3, y + 0.4), iso(x + w + 1.3, y + d + 0.4), iso(x + w, y + d)])} fill={SHADOW} />;
}

/** A farmhouse: white walls, a slate gable roof running along x. */
function House({ at: [x, y] }: { at: PlanePoint }) {
  const roof = useContext(ThemeContext).roof;
  const { w, d, h } = HOUSE;
  const ridge = h + 0.55;
  return (
    <g>
      <g data-kind="house">
      <polygon points={pts([iso(x, y + d / 2, ridge), iso(x + w, y + d / 2, ridge), iso(x + w, y, h), iso(x, y, h)])} fill={roof.back} />
      <polygon points={pts([iso(x, y + d, 0), iso(x + w, y + d, 0), iso(x + w, y + d, h), iso(x, y + d, h)])} fill="#f6f4ef" />
      <polygon points={pts([iso(x + w, y, 0), iso(x + w, y + d, 0), iso(x + w, y + d, h), iso(x + w, y + d / 2, ridge), iso(x + w, y, h)])} fill="#e4e2dc" />
      <polygon points={pts([iso(x + w * 0.18, y + d, 0), iso(x + w * 0.36, y + d, 0), iso(x + w * 0.36, y + d, 0.45), iso(x + w * 0.18, y + d, 0.45)])} fill="#8796a9" />
      <polygon points={pts([iso(x + w * 0.55, y + d, 0.28), iso(x + w * 0.8, y + d, 0.28), iso(x + w * 0.8, y + d, 0.5), iso(x + w * 0.55, y + d, 0.5)])} fill="#9cb6c3" />
      <polygon points={pts([iso(x - 0.08, y + d + 0.08, h - 0.05), iso(x + w + 0.08, y + d + 0.08, h - 0.05), iso(x + w + 0.08, y + d / 2, ridge), iso(x - 0.08, y + d / 2, ridge)])} fill={roof.slope} />
      </g>
    </g>
  );
}

/** A closed smooth curve through plane points (Catmull-Rom as cubic Béziers), scaled about a centre.
 *  The projection is affine, so a curve on the plane is the same curve on screen. */
function closedCurve(points: PlanePoint[], centre: PlanePoint, scale: number) {
  const p = points.map(([x, y]) => iso(centre[0] + (x - centre[0]) * scale, centre[1] + (y - centre[1]) * scale));
  const n = p.length;
  let d = `M ${p[0]![0]} ${p[0]![1]}`;
  for (let i = 0; i < n; i++) {
    const [p0, p1, p2, p3] = [p[(i - 1 + n) % n]!, p[i]!, p[(i + 1) % n]!, p[(i + 2) % n]!];
    d += ` C ${p1[0] + (p2[0] - p0[0]) / 6} ${p1[1] + (p2[1] - p0[1]) / 6} ${p2[0] - (p3[0] - p1[0]) / 6} ${p2[1] - (p3[1] - p1[1]) / 6} ${p2[0]} ${p2[1]}`;
  }
  return `${d} Z`;
}

/** A course through plane points with its corners rounded off. */
function roundedCourse(points: PlanePoint[], radius: number) {
  const at = ([x, y]: PlanePoint) => iso(x, y).join(" ");
  let d = `M ${at(points[0]!)}`;
  for (let i = 1; i < points.length - 1; i++) {
    const [a, c, b] = [points[i - 1]!, points[i]!, points[i + 1]!];
    const toward = (from: PlanePoint, to: PlanePoint, by: number): PlanePoint => {
      const len = Math.hypot(to[0] - from[0], to[1] - from[1]);
      return [from[0] + ((to[0] - from[0]) / len) * by, from[1] + ((to[1] - from[1]) / len) * by];
    };
    d += ` L ${at(toward(c, a, radius))} Q ${at(c)} ${at(toward(c, b, radius))}`;
  }
  return `${d} L ${at(points[points.length - 1]!)}`;
}

// Across a road or river, one plane unit is this many pixels on screen.
const UNIT_ACROSS = 13.856;

/** The lake and rivers as one surface: drawn through one mask, so the rivers run into the lake
 *  with no seam and everything that happens to the water happens to all of it. It shades from the
 *  sky's light in the distance to deeper water near us; every far bank (wherever land sits just
 *  above water on screen) casts a soft shadow onto it; and every tree is mirrored in it, so trees
 *  along a river reflect just as those around the lake do. */
function WaterBodies({ trees }: { trees: Tree[] }) {
  const water = useContext(ThemeContext).water;
  const lake = closedCurve(LAKE_SHORE, LAKE_CENTRE, 1);
  const rivers = RIVER_COURSES.map((c) => roundedCourse(c, 3));
  const ys = LAKE_SHORE.map(([x, y]) => iso(x, y)[1]);
  const [top, bottom] = [Math.min(...ys), Math.max(...ys)];
  // Everything water could cover, with room for the blur.
  const span = [...LAKE_SHORE, ...RIVER_COURSES.flat()].map(([x, y]) => iso(x, y));
  const pad = RIVER_WIDTH * UNIT_ACROSS + 40;
  const box = {
    x: Math.min(...span.map((p) => p[0])) - pad,
    y: Math.min(...span.map((p) => p[1])) - pad,
    width: Math.max(...span.map((p) => p[0])) - Math.min(...span.map((p) => p[0])) + pad * 2,
    height: Math.max(...span.map((p) => p[1])) - Math.min(...span.map((p) => p[1])) + pad * 2,
  };
  const shape = (fill: string) => (
    <>
      <path d={lake} fill={fill} />
      {rivers.map((d, i) => (
        <path key={i} d={d} fill="none" stroke={fill} strokeWidth={RIVER_WIDTH * UNIT_ACROSS} strokeLinecap="round" strokeLinejoin="round" />
      ))}
    </>
  );
  return (
    <g>
      <defs>
        <linearGradient id="ls-water" gradientUnits="userSpaceOnUse" x1={0} y1={top} x2={0} y2={bottom}>
          <stop offset="0" stopColor={water.far} />
          <stop offset="0.5" stopColor={water.mid} />
          <stop offset="1" stopColor={water.near} />
        </linearGradient>
        <mask id="ls-water-mask" maskUnits="userSpaceOnUse" {...box}>
          {shape("#fff")}
        </mask>
        <mask id="ls-land-mask" maskUnits="userSpaceOnUse" {...box}>
          <rect {...box} fill="#fff" />
          {shape("#000")}
        </mask>
        <filter id="ls-bank" x="-5%" y="-5%" width="110%" height="110%">
          <feGaussianBlur stdDeviation="3" />
        </filter>
      </defs>
      <g mask="url(#ls-water-mask)">
        <rect {...box} fill="url(#ls-water)" />
        {/* The trees' reflections: their spires upside down from the foot, in the water's own tone.
            A tree back from the bank shows only the part of its reflection that reaches water. */}
        <g opacity={0.5}>
          {trees.map((t, i) => {
            const [bx, by] = iso(t.at[0], t.at[1]);
            const half = 9.4 * t.size;
            return <polygon key={i} points={`${bx - half},${by} ${bx + half},${by} ${bx},${by + 24 * t.size}`} fill={water.reflection} />;
          })}
        </g>
        {/* The banks' shadow: the land, nudged down and softened, darkening the water below it. */}
        <g filter="url(#ls-bank)">
          <g transform="translate(0 8)">
            <rect {...box} fill={water.bank} fillOpacity={0.3} mask="url(#ls-land-mask)" />
          </g>
        </g>
      </g>
    </g>
  );
}

function Road({ r }: { r: Rect }) {
  return <polygon data-kind="road" points={quad(r)} fill={useContext(ThemeContext).road.fill} />;
}

/** Mountains on the horizon: hipped ridges in the same projection and light as the market, their
 *  feet lost in the haze. `depth` is x + y along the horizon. */
function Ridges({ depth, from, to }: { depth: number; from: number; to: number }) {
  const [light, shade] = useContext(ThemeContext).ridge;
  const rand = mulberry32(31);
  const ridges: { key: number; front: number; faces: [string, string]; along: boolean }[] = [];
  // Along the horizon, u = x - y runs left to right on screen.
  for (let u = from; u < to; ) {
    const d = depth - 1 - rand() * 4;
    const cx = (d + u) / 2;
    const cy = (d - u) / 2;
    const along = rand() < 0.5;
    const length = 6 + rand() * 9;
    const width = 3.4 + rand() * 3;
    const r = width / 2;
    const h = r * (1.3 + rand() * 1.1);
    let faces: [string, string];
    let front: number;
    if (along) {
      const [x0, x1, y0, y1, my] = [cx - length / 2, cx + length / 2, cy - r, cy + r, cy];
      faces = [
        pts([iso(x0, y1), iso(x1, y1), iso(x1 - r, my, h), iso(x0 + r, my, h)]),
        pts([iso(x1, y0), iso(x1, y1), iso(x1 - r, my, h)]),
      ];
      front = x1 + y1;
    } else {
      const [x0, x1, y0, y1, mx] = [cx - r, cx + r, cy - length / 2, cy + length / 2, cx];
      faces = [
        pts([iso(x0, y1), iso(x1, y1), iso(mx, y1 - r, h)]),
        pts([iso(x1, y0), iso(x1, y1), iso(mx, y1 - r, h), iso(mx, y0 + r, h)]),
      ];
      front = x1 + y1;
    }
    ridges.push({ key: u, front, faces, along });
    u += 4 + rand() * 6;
  }
  ridges.sort((a, b) => a.front - b.front);
  return (
    <g>
      {ridges.map((ridge) => (
        <g key={ridge.key}>
          <polygon data-kind="ridge" points={ridge.faces[0]} fill={light} />
          <polygon data-kind="ridge" points={ridge.faces[1]} fill={shade} />
        </g>
      ))}
    </g>
  );
}

// ---------------------------------------------------------------------------

/** The market's moving parts, each in its own small SVG on its own layer: our lot and the lots west
 *  of the road, the trucks on it, the lots east of it, then the price flags. A truck is moved by the
 *  GPU, so it glides at sub-pixel positions; a lot's dropping buildings and rolling doors redraw only
 *  that lot. The stage is the SVG view in its own units, scaled and centred the way the SVGs' "slice"
 *  fit does it, so each piece sits exactly where the full-screen layers would have drawn it. */
function MarketStage({ view }: { view: { x: number; y: number; w: number; h: number } }) {
  const [fit, setFit] = useState<{ s: number; tx: number; ty: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Layout size, not the on-screen box, so a transform on the page never puts the pieces off the
    // scene.
    const measure = () => {
      const [width, height] = [el.clientWidth, el.clientHeight];
      const s = Math.max(width / view.w, height / view.h);
      setFit({ s, tx: (width - view.w * s) / 2, ty: (height - view.h * s) / 2 });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [view.w, view.h]);
  const piece = ({ key, box, node }: MarketPiece, className = "") => (
    <div key={key} className={`absolute ${className}`} style={{ left: box.x - view.x, top: box.y - view.y, width: box.w, height: box.h, willChange: "transform" }}>
      <svg viewBox={`${box.x} ${box.y} ${box.w} ${box.h}`} width={box.w} height={box.h} className="ls-soft block overflow-visible">
        {node}
      </svg>
    </div>
  );
  return (
    <div ref={ref} className="absolute inset-0 overflow-hidden">
      {fit && (
        <div className="absolute left-0 top-0" style={{ width: view.w, height: view.h, transformOrigin: "0 0", transform: `translate(${fit.tx}px, ${fit.ty}px) scale(${fit.s})` }}>
          {MARKET_PIECES.back.map((p) => piece(p))}
          {TRUCKS.map(({ lane, heading }) => piece({ key: `truck ${lane}`, box: truckBox(lane, heading), node: <TruckBody lane={lane} heading={heading} /> }, driveClass(heading)))}
          {MARKET_PIECES.front.map((p) => piece(p))}
          {MARKET_PIECES.flags.map((p) => piece(p))}
        </div>
      )}
    </div>
  );
}

// The composition follows the hero's shape, not the screen's width: landscape screens get the wide
// view (copy left, market right), portrait ones the tall view (copy above, market below), so an iPad
// held upright gets the tall one like a phone.
const WIDE_QUERY = "(min-aspect-ratio: 1/1)";

function useWide() {
  const [wide, setWide] = useState(() => typeof window === "undefined" || window.matchMedia(WIDE_QUERY).matches);
  useEffect(() => {
    const query = window.matchMedia(WIDE_QUERY);
    const onChange = () => setWide(query.matches);
    onChange();
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);
  return wide;
}

/** A quiet horizon for the foot of a section: the hero's mountains in the same mist, their feet lost
 *  in a haze of `fade` (the colour below). Anchored to the bottom; wide screens crop the sky above,
 *  narrow ones crop the ends. */
export function Horizon({ className = "", fade }: { className?: string; fade: string }) {
  const view = { x: -640, y: -175, w: 1280, h: 215 };
  return (
    <ThemeContext.Provider value={MIST}>
      <svg viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`} preserveAspectRatio="xMidYMax slice" className={`ls-soft ${className}`} aria-hidden>
        <defs>
          <linearGradient id="hz-haze" gradientUnits="userSpaceOnUse" x1={0} y1={-95} x2={0} y2={10}>
            <stop offset="0" stopColor={fade} stopOpacity="0" />
            <stop offset="0.75" stopColor={fade} stopOpacity="0.9" />
            <stop offset="1" stopColor={fade} stopOpacity="1" />
          </linearGradient>
        </defs>
        <Ridges depth={0} from={view.x / 13.856 - 12} to={(view.x + view.w) / 13.856 + 12} />
        <rect x={view.x} y={-95} width={view.w} height={view.h} fill="url(#hz-haze)" />
      </svg>
    </ThemeContext.Provider>
  );
}

export function LandscapeStyles() {
  return (
    <style>{`
      /* The scene's layers are painted once and never animated as a whole: a white veil over them
         fades away instead. In Safari a layer whose own animation ends is repainted (the land's
         thousands of shapes, a stall mid-entrance); the veil is one white box. */
      .ls-layer { position: absolute; inset: 0; }
      @keyframes ls-unveil { from { opacity: 1; } to { opacity: 0; } }
      .ls-veil { position: absolute; inset: 0; background: #fff; pointer-events: none; will-change: opacity; animation: ls-unveil 1100ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }
      .ls-layer > svg, .ls-shadows > svg { width: 100%; height: 100%; display: block; }
      /* The market's shadows fade in as one layer while the buildings drop: an HTML layer's opacity,
         so the blur is drawn once and never again (Safari redraws an SVG's filters whenever anything
         in that SVG moves). */
      .ls-shadows { position: absolute; inset: 0; will-change: opacity; animation: bb-fade ${LANDSCAPE_ENTRANCE.landed - ENTRANCE.first}ms linear ${ENTRANCE.first}ms both; }
      /* Outer facilities are part of the still land: no drops, doors shut. */
      .ls-still * { animation: none !important; }
      .ls-still .bb-swap-b { opacity: 0; }
    `}</style>
  );
}

export function IsoLandscape({ className = "" }: { className?: string }) {
  const wide = useWide();
  const theme = MIST;
  const market = (part: MarketInLandscape["part"]): MarketInLandscape => ({ ours: OURS, rivals: RIVALS, reach: 15.5, pad: theme.pad, ourPad: theme.ourPad, part });
  // The view: the market right of centre on wide screens, low and centred on narrow ones; the
  // horizon a third of the way down, where the country fades into the sky.
  const view = wide ? { w: 1280, h: 800, mx: 0.68, my: 0.68, horizon: 0.33 } : { w: 620, h: 1120, mx: 0.5, my: 0.74, horizon: 0.4 };
  const [cx, cy] = iso(CENTRE[0], CENTRE[1]);
  const x0 = cx - view.mx * view.w;
  const y0 = cy - view.my * view.h;
  const horizon = y0 + view.horizon * view.h;
  const x1 = x0 + view.w;
  const front = (p: PlanePoint) => p[0] + p[1];

  // Trees and houses stay out from behind the headline and its copy on wide screens.
  const clearOfCopy = ([px, py]: PlanePoint) => {
    if (!wide) return true;
    const [sx, sy] = iso(px, py);
    return !((sx - x0) / view.w < 0.5 && (sy - y0) / view.h > 0.22 && (sy - y0) / view.h < 0.56);
  };
  // Far to near: everything behind the market (by x + y), the market, then everything in front.
  const trees = COUNTRY.trees.filter((t) => clearOfCopy(t.at));
  const houses = HOUSES.filter((h) => clearOfCopy(h));
  const things: { depth: number; node: ReactNode }[] = [
    ...trees.map((t, i) => ({ depth: front(t.at), node: <TreeShape key={`t${i}`} t={t} /> })),
    ...houses.map((h, i) => ({ depth: front(h) + 2.9, node: <House key={`h${i}`} at={h} /> })),
    ...OUTER.flatMap((f, fi) =>
      f.buildings.map((b, bi) => ({
        depth: b.x + b.w + b.y + b.d,
        node: (
          <g key={`o${fi}-${bi}`}>
            <g filter="url(#ls-soft)">
              <Shadow b={b} />
            </g>
            {b.floors ? <Tower b={b} delay={0} /> : <DriveUpRow b={b} delay={0} />}
          </g>
        ),
      })),
    ),
  ].sort((a, b) => a.depth - b.depth);
  const behind = things.filter((t) => t.depth <= 20);
  const ahead = things.filter((t) => t.depth > 20);

  // Three stacked SVGs with the same view, so they line up exactly: the still land behind the market,
  // the market with its animations, and the still trees in front of it.
  const viewBox = `${x0} ${y0} ${view.w} ${view.h}`;
  const layer = (still: boolean, children: ReactNode) => (
    <div className={`ls-layer${still ? " ls-still" : ""}`}>
      <svg viewBox={viewBox} preserveAspectRatio="xMidYMid slice" className="ls-soft" aria-hidden>
        {children}
      </svg>
    </div>
  );

  return (
    <ThemeContext.Provider value={theme}>
      <div className={className} style={{ "--bb-loops": `${LANDSCAPE_ENTRANCE.settled}ms` } as CSSProperties} aria-hidden>
        <style>{SOFT_EDGES}</style>
        {layer(
          true,
          <>
          <defs>
            <filter id="ls-soft" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="1.8" />
            </filter>
            <linearGradient id="ls-sky" gradientUnits="userSpaceOnUse" x1={0} y1={y0} x2={0} y2={horizon + 20}>
              <stop offset="0" stopColor={theme.sky[0]} />
              <stop offset="0.55" stopColor={theme.sky[1]} />
              <stop offset="1" stopColor={theme.sky[2]} />
            </linearGradient>
            <linearGradient id="ls-haze" gradientUnits="userSpaceOnUse" x1={0} y1={horizon - 110} x2={0} y2={horizon + 10}>
              <stop offset="0" stopColor={theme.sky[2]} stopOpacity="0" />
              <stop offset="0.7" stopColor={theme.sky[2]} stopOpacity="0.85" />
              <stop offset="1" stopColor={theme.sky[2]} stopOpacity="1" />
            </linearGradient>
            <linearGradient id="ls-fade" gradientUnits="userSpaceOnUse" x1={0} y1={horizon} x2={0} y2={horizon + 120}>
              <stop offset="0" stopColor="#fff" stopOpacity="0" />
              <stop offset="1" stopColor="#fff" stopOpacity="1" />
            </linearGradient>
            <mask id="ls-ground" maskUnits="userSpaceOnUse" x={x0} y={y0} width={view.w} height={view.h}>
              <rect x={x0} y={y0} width={view.w} height={view.h} fill="url(#ls-fade)" />
            </mask>
          </defs>

            {/* Sky and mountains. */}
            <rect x={x0} y={y0} width={view.w} height={view.h} fill="url(#ls-sky)" />
            <Ridges depth={horizon / 8} from={x0 / 13.856 - 12} to={x1 / 13.856 + 12} />
            <rect x={x0} y={horizon - 110} width={view.w} height={view.h} fill="url(#ls-haze)" />

            {/* The country, fading in from the horizon. */}
            <g mask="url(#ls-ground)">
              <polygon data-kind="ground" points={quad(WORLD)} fill={theme.ground} />
              {COUNTRY.parcels.map((p, i) => (
                <Field key={i} p={p} />
              ))}
              <WaterBodies trees={trees} />
              {ROADS.map((r) => (
                <Road key={r.join()} r={r} />
              ))}
              {OUTER.map((f) => (
                <polygon key={f.lot.join()} points={pts(octagon(f.lot, 0.7).map(([px, py]) => iso(px, py)))} fill={theme.pad} data-kind="pad" />
              ))}
              <g filter="url(#ls-soft)">
                {trees.map((t, i) => (
                  <TreeShadow key={i} t={t} />
                ))}
                {houses.map((h, i) => (
                  <HouseShadow key={i} at={h} />
                ))}
              </g>
              {behind.map((t) => t.node)}
            </g>
          </>,
        )}
        <div className="ls-layer">
          <div className="ls-shadows">
            <svg viewBox={viewBox} preserveAspectRatio="xMidYMid slice" className="ls-soft" aria-hidden>
              <IsoMarketScene landscape={market("shadows")} />
            </svg>
          </div>
        </div>
        {layer(true, <IsoMarketScene landscape={market("ground")} />)}
        <div className="ls-layer">
          <MarketStage view={{ x: x0, y: y0, w: view.w, h: view.h }} />
        </div>
        {layer(true, ahead.map((t) => t.node))}
        <div className="ls-veil" />
      </div>
    </ThemeContext.Provider>
  );
}
